import { format, parse } from "date-fns"
import { formatInTimeZone, fromZonedTime, toZonedTime } from "date-fns-tz"

import { getStoredValue, setStoredValue } from "@/lib/safe-storage"

/**
 * Fallback timezone list for dropdowns when shared server data is unavailable.
 */
export const TIMEZONES = [
  ["Pacific Time (US & Canada)", "America/Los_Angeles"],
  ["Mountain Time (US & Canada)", "America/Denver"],
  ["Central Time (US & Canada)", "America/Chicago"],
  ["Eastern Time (US & Canada)", "America/New_York"],
  ["Hawaii", "Pacific/Honolulu"],
  ["Alaska", "America/Anchorage"],
  ["Arizona", "America/Phoenix"],
  ["London", "Europe/London"],
  ["Paris", "Europe/Paris"],
  ["Berlin", "Europe/Berlin"],
  ["Amsterdam", "Europe/Amsterdam"],
  ["Rome", "Europe/Rome"],
  ["Madrid", "Europe/Madrid"],
  ["Zurich", "Europe/Zurich"],
  ["Vienna", "Europe/Vienna"],
  ["Stockholm", "Europe/Stockholm"],
  ["Moscow", "Europe/Moscow"],
  ["Dubai", "Asia/Dubai"],
  ["Mumbai", "Asia/Kolkata"],
  ["Singapore", "Asia/Singapore"],
  ["Hong Kong", "Asia/Hong_Kong"],
  ["Tokyo", "Asia/Tokyo"],
  ["Seoul", "Asia/Seoul"],
  ["Sydney", "Australia/Sydney"],
  ["Melbourne", "Australia/Melbourne"],
  ["Auckland", "Pacific/Auckland"],
  ["Sao Paulo", "America/Sao_Paulo"],
  ["Mexico City", "America/Mexico_City"],
  ["UTC", "UTC"],
] as const

export type TimezoneIdentifier = string

export const DEFAULT_TIMEZONE: TimezoneIdentifier = "America/Los_Angeles"

function formatUtcOffsetForTimezone(identifier: string, date: Date = new Date()): string | null {
  try {
    const offsetWithColon = formatInTimeZone(date, identifier, "xxx")
    if (!offsetWithColon || offsetWithColon === "Z") return "+00:00"
    return offsetWithColon
  } catch {
    return null
  }
}

function humanizeTimezoneIdentifier(identifier: string): string {
  if (identifier === "UTC" || identifier === "Etc/UTC") return "UTC"

  const parts = identifier.split("/")
  const cityPart = parts[parts.length - 1] || identifier
  return cityPart.replace(/_/g, " ")
}

/**
 * Detect user's timezone from browser
 * Returns IANA timezone identifier like "America/Los_Angeles"
 */
export function detectTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    return DEFAULT_TIMEZONE
  }
}

/**
 * Get display name for a timezone identifier
 */
export function getTimezoneDisplay(identifier: string): string {
  const found = TIMEZONES.find(([, id]) => id === identifier)
  if (found) return found[0]

  const offset = formatUtcOffsetForTimezone(identifier)
  if (!offset) return identifier

  return `${humanizeTimezoneIdentifier(identifier)} (UTC${offset})`
}

/**
 * Get short display name for a timezone identifier
 */
export function getTimezoneShortDisplay(identifier: string): string {
  const display = getTimezoneDisplay(identifier)
  // Extract just the main part for shorter display
  if (display.includes("(")) {
    return display.split("(")[0].trim()
  }
  return display
}

/**
 * Get timezone from localStorage (for unauthenticated users)
 */
export function getStoredTimezone(): string | null {
  return getStoredValue("user_timezone")
}

/**
 * Store timezone in localStorage
 */
export function storeTimezone(timezone: string): void {
  setStoredValue("user_timezone", timezone)
}

/**
 * Get effective timezone (user preference > localStorage > browser detection)
 */
export function getEffectiveTimezone(userTimezone?: string | null): string {
  if (userTimezone) return userTimezone
  return getStoredTimezone() || detectTimezone()
}

/**
 * Get timezone abbreviation (e.g., "PST", "EST")
 */
export function getTimezoneAbbreviation(
  identifier: string,
  date: Date = new Date()
): string {
  try {
    return formatInTimeZone(date, identifier, "zzz")
  } catch {
    return identifier
  }
}

interface ConvertedTime {
  time: string // Formatted time in user's timezone (e.g., "3:30 AM")
  date: string // Date in user's timezone (e.g., "January 16, 2026")
  dateShort: string // Short date (e.g., "Jan 16")
  dateISO: string // ISO date string (e.g., "2026-01-16")
  crossed: boolean // Whether the date shifted to a different day
  tzAbbrev: string // Timezone abbreviation
}

/**
 * Convert an event time from one timezone to another
 *
 * @param timeStr Time in HH:MM format (24-hour, e.g., "10:30") or "h:mm a" format (e.g., "10:30 AM")
 * @param dateStr Date in YYYY-MM-DD format
 * @param fromTz Source timezone identifier (event's timezone)
 * @param toTz Target timezone identifier (user's timezone)
 * @returns Converted time information
 */
export function convertEventTime(
  timeStr: string,
  dateStr: string,
  fromTz: string,
  toTz: string
): ConvertedTime {
  // Parse the time - handle both "HH:mm" and "h:mm a" formats
  let hours: number
  let minutes: number

  if (timeStr.includes("AM") || timeStr.includes("PM")) {
    // Parse 12-hour format
    const parsed = parse(timeStr, "h:mm a", new Date())
    hours = parsed.getHours()
    minutes = parsed.getMinutes()
  } else {
    // Parse 24-hour format
    const [h, m] = timeStr.split(":").map(Number)
    hours = h
    minutes = m
  }

  // Create a datetime in the source timezone
  const sourceDateTime = fromZonedTime(
    new Date(`${dateStr}T${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:00`),
    fromTz
  )

  // Convert to target timezone
  const targetDateTime = toZonedTime(sourceDateTime, toTz)

  // Format the results
  const time = formatInTimeZone(sourceDateTime, toTz, "h:mm a")
  const date = formatInTimeZone(sourceDateTime, toTz, "MMMM d, yyyy")
  const dateShort = formatInTimeZone(sourceDateTime, toTz, "MMM d")
  const dateISO = formatInTimeZone(sourceDateTime, toTz, "yyyy-MM-dd")
  const tzAbbrev = getTimezoneAbbreviation(toTz, sourceDateTime)

  // Check if date crossed (different day in user's timezone)
  const crossed = dateISO !== dateStr

  return { time, date, dateShort, dateISO, crossed, tzAbbrev }
}

/**
 * Format time for display with optional timezone conversion
 *
 * @param timeStr Time in HH:MM format or "h:mm a" format
 * @param eventTz Event's timezone identifier
 * @param userTz User's timezone identifier
 * @param dateStr Optional date in YYYY-MM-DD format (required for accurate conversion)
 * @returns Formatted display object
 */
export function formatTimeWithTimezone(
  timeStr: string,
  eventTz: string,
  userTz: string,
  dateStr?: string
): {
  userTime: string
  eventTime: string
  userTzAbbrev: string
  eventTzAbbrev: string
  sameTimezone: boolean
  dateCrossed: boolean
  userDate?: string
} {
  // Format the original event time
  let eventTime: string
  if (timeStr.includes("AM") || timeStr.includes("PM")) {
    eventTime = timeStr
  } else {
    const [h, m] = timeStr.split(":").map(Number)
    const period = h >= 12 ? "PM" : "AM"
    const displayHours = h % 12 || 12
    eventTime = `${displayHours}:${m.toString().padStart(2, "0")} ${period}`
  }

  const eventTzAbbrev = getTimezoneAbbreviation(eventTz)

  // Same timezone - no conversion needed
  if (eventTz === userTz) {
    return {
      userTime: eventTime,
      eventTime,
      userTzAbbrev: eventTzAbbrev,
      eventTzAbbrev,
      sameTimezone: true,
      dateCrossed: false,
    }
  }

  // Different timezones - need to convert
  const today = dateStr || format(new Date(), "yyyy-MM-dd")
  const converted = convertEventTime(timeStr, today, eventTz, userTz)

  return {
    userTime: converted.time,
    eventTime,
    userTzAbbrev: converted.tzAbbrev,
    eventTzAbbrev,
    sameTimezone: false,
    dateCrossed: converted.crossed,
    userDate: converted.crossed ? converted.dateShort : undefined,
  }
}

/**
 * Check if an event has a specific time (as opposed to date-only)
 * Events with start_time_raw should have their times converted
 * Events without should show dates as-is with timezone label
 */
export function eventHasTime(
  event: { start_time_raw?: string | null; has_time?: boolean } | null | undefined
): boolean {
  return !!(event?.start_time_raw || event?.has_time)
}

/**
 * Format a date range with timezone label (for date-only events)
 */
export function formatDateRangeWithTimezone(
  dateRange: string,
  timezoneDisplay: string
): string {
  return `${dateRange} (${timezoneDisplay})`
}

/**
 * Format a time range for display with timezone conversion
 * Converts both start and end times from event timezone to user timezone
 *
 * @param startTimeStr Start time in HH:MM format
 * @param endTimeStr End time in HH:MM format
 * @param eventTz Event's timezone identifier
 * @param userTz User's timezone identifier
 * @param dateStr Optional date in YYYY-MM-DD format
 * @returns Formatted time range info
 */
export function formatTimeRangeWithTimezone(
  startTimeStr: string,
  endTimeStr: string,
  eventTz: string,
  userTz: string,
  dateStr?: string
): {
  userTimeRange: string
  eventTimeRange: string
  userTzAbbrev: string
  eventTzAbbrev: string
  sameTimezone: boolean
  dateCrossed: boolean
  userDate?: string
} {
  // Format the original event time range
  const formatTime12h = (timeStr: string): { display: string; period: string } => {
    if (timeStr.includes("AM") || timeStr.includes("PM")) {
      const parts = timeStr.split(" ")
      return { display: parts[0], period: parts[1] }
    }
    const [h, m] = timeStr.split(":").map(Number)
    const period = h >= 12 ? "PM" : "AM"
    const displayHours = h % 12 || 12
    return { display: `${displayHours}:${m.toString().padStart(2, "0")}`, period }
  }

  const startFormatted = formatTime12h(startTimeStr)
  const endFormatted = formatTime12h(endTimeStr)

  // Format event time range: "10:30-11:30 AM" or "10:30 AM-12:30 PM"
  const eventTimeRange =
    startFormatted.period === endFormatted.period
      ? `${startFormatted.display}-${endFormatted.display} ${startFormatted.period}`
      : `${startFormatted.display} ${startFormatted.period}-${endFormatted.display} ${endFormatted.period}`

  const eventTzAbbrev = getTimezoneAbbreviation(eventTz)

  // Same timezone - no conversion needed
  if (eventTz === userTz) {
    return {
      userTimeRange: eventTimeRange,
      eventTimeRange,
      userTzAbbrev: eventTzAbbrev,
      eventTzAbbrev,
      sameTimezone: true,
      dateCrossed: false,
    }
  }

  // Different timezones - need to convert both times
  const today = dateStr || format(new Date(), "yyyy-MM-dd")
  const startConverted = convertEventTime(startTimeStr, today, eventTz, userTz)
  const endConverted = convertEventTime(endTimeStr, today, eventTz, userTz)

  // Format user time range
  const startUserFormatted = formatTime12h(startConverted.time)
  const endUserFormatted = formatTime12h(endConverted.time)

  const userTimeRange =
    startUserFormatted.period === endUserFormatted.period
      ? `${startUserFormatted.display}-${endUserFormatted.display} ${startUserFormatted.period}`
      : `${startUserFormatted.display} ${startUserFormatted.period}-${endUserFormatted.display} ${endUserFormatted.period}`

  // Check if date crossed for either time
  const dateCrossed = startConverted.crossed || endConverted.crossed

  return {
    userTimeRange,
    eventTimeRange,
    userTzAbbrev: startConverted.tzAbbrev,
    eventTzAbbrev,
    sameTimezone: false,
    dateCrossed,
    userDate: dateCrossed ? startConverted.dateShort : undefined,
  }
}

function formatTime12h(timeStr: string): { display: string; period: string } {
  if (timeStr.includes("AM") || timeStr.includes("PM")) {
    const parts = timeStr.split(" ")
    return { display: parts[0], period: parts[1] }
  }

  const [h, m] = timeStr.split(":").map(Number)
  const period = h >= 12 ? "PM" : "AM"
  const displayHours = h % 12 || 12
  return { display: `${displayHours}:${m.toString().padStart(2, "0")}`, period }
}

export function formatEventTime(timeStr: string): string {
  const formatted = formatTime12h(timeStr)
  return `${formatted.display} ${formatted.period}`
}

export function formatEventTimeRange(startTimeStr: string, endTimeStr: string): string {
  const startFormatted = formatTime12h(startTimeStr)
  const endFormatted = formatTime12h(endTimeStr)

  return startFormatted.period === endFormatted.period
    ? `${startFormatted.display}-${endFormatted.display} ${startFormatted.period}`
    : `${startFormatted.display} ${startFormatted.period}-${endFormatted.display} ${endFormatted.period}`
}

export function formatEventLocalTimeLabel({
  startTimeRaw,
  endTimeRaw,
  formattedTimeRange,
  formattedStartTime,
  timezoneAbbreviation,
  timezoneDisplay,
}: {
  startTimeRaw?: string | null
  endTimeRaw?: string | null
  formattedTimeRange?: string | null
  formattedStartTime?: string | null
  timezoneAbbreviation?: string | null
  timezoneDisplay?: string | null
}): string | null {
  const timezoneLabel = timezoneAbbreviation || timezoneDisplay

  if (startTimeRaw && endTimeRaw) {
    const timeRange = formatEventTimeRange(startTimeRaw, endTimeRaw)
    return timezoneLabel ? `${timeRange} (${timezoneLabel})` : timeRange
  }

  if (startTimeRaw) {
    const time = formatEventTime(startTimeRaw)
    return timezoneLabel ? `${time} (${timezoneLabel})` : time
  }

  if (formattedTimeRange) {
    return timezoneLabel ? `${formattedTimeRange} (${timezoneLabel})` : formattedTimeRange
  }

  if (formattedStartTime) {
    return timezoneLabel ? `${formattedStartTime} (${timezoneLabel})` : formattedStartTime
  }

  return null
}
